/**
 * 注册阻断弹窗组件（区域适配版）
 * 根据用户所在区域显示对应的协议链接
 *
 * 功能：
 * - 从 RegionStore 读取区域信息
 * - 显示当前区域对应的协议链接
 * - 支持多语言
 * - 记录用户同意的协议版本和区域
 *
 * **Validates: Requirements 5.4**
 */

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useRegionStore,
  useRegionLinks,
  useRegion,
} from "@/stores/regionStore";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { ExternalLink } from "lucide-react";

export interface RegistrationBlockingModalProps {
  /** 弹窗是否打开 */
  open: boolean;
  /** 关闭弹窗回调 */
  onOpenChange: (open: boolean) => void;
  /** 用户同意回调，传递协议 ID 列表 */
  onAgree: (agreementIds?: number[]) => void;
  /** 用户拒绝回调 */
  onDisagree: () => void;
  /** 协议列表（可选，用于记录签署的协议 ID） */
  agreements?: Array<{ id: number; type: string; version: string }>;
}

/**
 * 注册阻断弹窗（区域适配版）
 *
 * 功能：
 * - 显示温馨提示标题
 * - 显示协议阅读提示正文
 * - 根据区域显示对应的协议链接
 * - 提供"同意并继续"和"不同意并退出"按钮
 * - 支持键盘导航和无障碍访问
 * - 支持 Escape 键关闭（等同于点击"不同意并退出"）
 */
export default function RegistrationBlockingModal({
  open,
  onOpenChange,
  onAgree,
  onDisagree,
  agreements = [],
}: RegistrationBlockingModalProps) {
  const { t } = useTranslation(["consent", "common"]);
  const [checked, setChecked] = useState(false);

  // 从 RegionStore 读取区域信息
  const links = useRegionLinks();
  const region = useRegion();
  const { bootstrapData } = useRegionStore();

  // 构建协议链接
  // 优先使用 Bootstrap API 返回的动态链接，否则使用静态链接
  const termsUrl = links?.terms || `/terms?region=${region}`;
  const privacyUrl = links?.privacy || `/privacy?region=${region}`;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // 关闭弹窗时触发拒绝回调
      onDisagree();
      setChecked(false);
    }
    onOpenChange(newOpen);
  };

  const handleAgree = () => {
    if (!checked) return;

    // 提取协议 ID 列表
    const agreementIds = agreements
      .filter((a) => a.type === "tos" || a.type === "privacy")
      .map((a) => a.id);

    onAgree(agreementIds.length > 0 ? agreementIds : undefined);
    onOpenChange(false);
    setChecked(false);
  };

  const handleDisagree = () => {
    onDisagree();
    onOpenChange(false);
    setChecked(false);
  };

  // 获取法律实体名称
  const legalEntity = bootstrapData?.legal_entity || "Tunzo";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        aria-labelledby="registration-blocking-title"
        aria-describedby="registration-blocking-description"
      >
        <DialogHeader>
          <DialogTitle id="registration-blocking-title">
            {t("consent:blockingModal.title", "温馨提示")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <DialogDescription
            id="registration-blocking-description"
            className="text-sm leading-relaxed"
          >
            {t(
              "consent:blockingModal.description",
              `欢迎使用 ${legalEntity}。为保障您的合法权益，请您在继续下一步操作前，仔细阅读并充分理解`
            )}
          </DialogDescription>

          {/* 协议链接 */}
          <div className="flex flex-col gap-2">
            <a
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline font-medium text-sm"
            >
              {t("consent:blockingModal.tosLink", "《用户服务协议》")}
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline font-medium text-sm"
            >
              {t("consent:blockingModal.privacyLink", "《隐私政策》")}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {/* 区域信息提示 */}
          {region && region !== "GLOBAL" && (
            <p className="text-xs text-muted-foreground">
              {t("consent:blockingModal.regionHint", {
                region: region,
                defaultValue: `当前区域：${region}`,
              })}
            </p>
          )}

          {/* 同意勾选框 */}
          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="agreement-checkbox"
              checked={checked}
              onCheckedChange={(value) => setChecked(value === true)}
              aria-describedby="agreement-checkbox-description"
            />
            <label
              htmlFor="agreement-checkbox"
              id="agreement-checkbox-description"
              className="text-sm leading-relaxed cursor-pointer"
            >
              {t(
                "consent:blockingModal.checkboxLabel",
                "我已阅读并同意上述协议"
              )}
            </label>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleDisagree}
            className="w-full sm:w-auto"
          >
            {t("consent:blockingModal.disagreeButton", "不同意并退出")}
          </Button>
          <Button
            onClick={handleAgree}
            className="w-full sm:w-auto"
            disabled={!checked}
          >
            {t("consent:blockingModal.agreeButton", "同意并继续")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
